import { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Beaker, Trash2, Plus, Mail } from 'lucide-react';
import OpsModal from '@/components/ops/OpsModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } });

const MERGE_FIELDS = [
  ['client_first_name', "Client's first name"],
  ['client_last_name', "Client's last name"],
  ['loved_one_first_name', "Loved one's first name"],
  ['loved_one_pronouns', "Loved one's pronouns"],
  ['interview_date', 'Interview date'],
  ['interview_time', 'Interview time'],
  ['interview_location', 'Interview location'],
  ['where_in_home', 'Where in home'],
  ['who_will_be_present', 'Who will be present'],
  ['interviewer_name', 'Interviewer name'],
  ['interviewer_phone', 'Interviewer phone'],
  ['interviewer_email', 'Interviewer email'],
  ['selection_link', 'Selection link'],
  ['selection_deadline', 'Selection deadline'],
];

export default function TemplateEditModal({ template, onClose, onSaved }) {
  const isCreate = !template;
  const isSystem = !!template?.is_system;
  const hasAdvanced = !!(template?.has_advanced_logic) || template?.key === 'post_discovery_followup';

  const [name, setName] = useState(template?.name || '');
  const [description, setDescription] = useState(template?.notes || template?.description || '');
  const [subjects, setSubjects] = useState(template?.subjects?.length ? template.subjects : ['']);
  const [body, setBody] = useState(template?.body || '');
  const [interviewerVisible, setInterviewerVisible] = useState(template?.interviewer_visible ?? true);
  const [active, setActive] = useState(template?.active ?? true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const bodyRef = useRef(null);

  const insertMerge = (field) => {
    const el = bodyRef.current;
    if (!el) { setBody(b => b + ` {${field}}`); return; }
    const start = el.selectionStart, end = el.selectionEnd;
    const insert = `{${field}}`;
    const next = body.slice(0, start) + insert + body.slice(end);
    setBody(next);
    requestAnimationFrame(() => {
      el.focus();
      el.selectionStart = el.selectionEnd = start + insert.length;
    });
  };

  const updateSubject = (i, v) => setSubjects(s => s.map((x, idx) => idx === i ? v : x));
  const addSubject = () => setSubjects(s => s.length < 3 ? [...s, ''] : s);
  const removeSubject = (i) => setSubjects(s => s.length > 1 ? s.filter((_, idx) => idx !== i) : s);

  const save = async () => {
    if (!name.trim() || !body.trim() || subjects.every(s => !s.trim())) {
      toast.error('Name, subject, and body are required'); return;
    }
    setSaving(true);
    try {
      if (isCreate) {
        await axios.post(`${API}/ops/email-templates`, {
          name: name.trim(),
          description: description.trim(),
          subject: subjects[0],
          body,
          interviewer_visible: interviewerVisible,
          active,
        }, getAuth());
        toast.success('Template created');
      } else {
        await axios.put(`${API}/ops/email-templates/${template.id}`, {
          name: name.trim(),
          subjects: subjects.filter(s => s.trim()),
          body,
          notes: description.trim(),
          interviewer_visible: interviewerVisible,
          active,
        }, getAuth());
        toast.success('Template updated');
      }
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const testSend = async () => {
    if (isCreate) { toast.error('Save the template first, then test send'); return; }
    setTesting(true);
    try {
      const { data } = await axios.post(`${API}/ops/email-templates/${template.id}/test-send`, {}, getAuth());
      toast.success(`Test send sent to ${data.to}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Test send failed'); }
    finally { setTesting(false); }
  };

  const remove = async () => {
    if (!window.confirm('Delete this template? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/ops/email-templates/${template.id}`, getAuth());
      toast.success('Template deleted'); onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const headerActions = isSystem ? (
    <span className="ops-pill bg-slate-100 text-slate-600 border-slate-200">System</span>
  ) : !isCreate ? (
    <span className="ops-pill bg-emerald-50 text-emerald-700 border-emerald-100">Custom</span>
  ) : null;

  const footer = (
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <div className="flex items-center gap-2">
        {!isCreate && (
          <button
            onClick={testSend}
            disabled={testing}
            data-testid="test-send-button"
            className="h-9 inline-flex items-center gap-1.5 text-[12px] text-slate-700 hover:text-slate-900 bg-white hover:bg-slate-50 border border-slate-200 hover:border-slate-300 rounded-md px-3 font-medium disabled:opacity-50 transition-colors"
          >
            <Beaker size={12} /> {testing ? 'Sending…' : 'Test send to me'}
          </button>
        )}
        {!isCreate && !isSystem && (
          <button
            onClick={remove}
            data-testid="delete-template-button"
            className="h-9 inline-flex items-center gap-1.5 text-[12px] text-red-700 hover:text-red-800 hover:bg-red-50 border border-slate-200 hover:border-red-200 rounded-md px-3 font-medium transition-colors"
          >
            <Trash2 size={12} /> Delete
          </button>
        )}
      </div>
      <div className="flex items-center gap-2">
        <button onClick={onClose} className="h-9 text-[13px] text-slate-700 hover:text-slate-900 hover:bg-slate-100 border border-slate-200 rounded-md px-3 transition-colors">Cancel</button>
        <button
          onClick={save}
          disabled={saving}
          data-testid="save-template-button"
          className="h-9 bg-slate-900 hover:bg-slate-800 text-white px-4 rounded-md text-[13px] font-medium shadow-sm disabled:opacity-50 transition-colors"
        >
          {saving ? 'Saving…' : isCreate ? 'Create template' : 'Save changes'}
        </button>
      </div>
    </div>
  );

  return (
    <OpsModal
      title={isCreate ? 'New template' : (isSystem ? 'Edit system template' : 'Edit template')}
      subtitle={isCreate ? 'Custom email template' : template?.key && (<code className="text-[11px] font-mono text-slate-500">{template.key}</code>)}
      icon={Mail}
      onClose={onClose}
      size="xl"
      headerActions={headerActions}
      footer={footer}
      testid="template-edit-modal"
    >
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_260px] divide-y lg:divide-y-0 lg:divide-x divide-slate-200">

        {/* MAIN COLUMN */}
        <div className="px-5 py-4 space-y-5">

          {/* General */}
          <section>
            <p className="text-[11px] uppercase tracking-wider font-medium text-slate-500 mb-2">General</p>
            <div className="space-y-3">
              <div>
                <label className="text-[11px] uppercase tracking-wider font-medium text-slate-500 block mb-1">Name *</label>
                <input
                  value={name}
                  onChange={e => setName(e.target.value)}
                  disabled={isSystem}
                  data-testid="template-name-input"
                  className="w-full h-8 bg-white border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 rounded-md px-2 text-[13px] text-slate-900 outline-none transition-colors disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed"
                />
                {isSystem && <p className="text-[11px] text-slate-500 mt-1">System template names are locked.</p>}
              </div>
              {template?.key && (
                <div>
                  <label className="text-[11px] uppercase tracking-wider font-medium text-slate-500 block mb-1">Key · locked</label>
                  <code className="block text-[12px] font-mono text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-2 py-1.5">{template.key}</code>
                </div>
              )}
              <div>
                <label className="text-[11px] uppercase tracking-wider font-medium text-slate-500 block mb-1">Description · internal notes</label>
                <textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  rows={2}
                  data-testid="template-description-input"
                  className="w-full bg-white border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 rounded-md px-2 py-1.5 text-[13px] text-slate-900 outline-none resize-none leading-relaxed transition-colors"
                />
              </div>
            </div>
          </section>

          {/* Subjects */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[11px] uppercase tracking-wider font-medium text-slate-500">
                Subject{subjects.length > 1 ? ` variants · ${subjects.length}` : ' line'}
              </p>
              {subjects.length < 3 && !isCreate && (
                <button
                  type="button"
                  onClick={addSubject}
                  data-testid="add-subject-button"
                  className="inline-flex items-center gap-1 text-[12px] text-slate-700 hover:text-slate-900 hover:underline"
                >
                  <Plus size={11} /> Add variant
                </button>
              )}
            </div>
            <div className="space-y-1.5">
              {subjects.map((s, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    value={s}
                    onChange={e => updateSubject(i, e.target.value)}
                    data-testid={`template-subject-${i}-input`}
                    placeholder={`Subject variant ${i + 1}`}
                    className="flex-1 h-8 bg-white border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 rounded-md px-2 text-[13px] text-slate-900 outline-none placeholder:text-slate-400 transition-colors"
                  />
                  {subjects.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeSubject(i)}
                      data-testid={`remove-subject-${i}`}
                      className="text-slate-400 hover:text-red-600 hover:bg-red-50 rounded p-1 transition-colors"
                      aria-label="Remove subject variant"
                    >
                      <X size={13} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Body */}
          <section>
            <p className="text-[11px] uppercase tracking-wider font-medium text-slate-500 mb-1.5">Body</p>
            <textarea
              ref={bodyRef}
              value={body}
              onChange={e => setBody(e.target.value)}
              rows={16}
              data-testid="template-body-textarea"
              className="w-full bg-white border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 rounded-md px-3 py-2.5 text-[13px] text-slate-800 outline-none resize-none font-mono leading-relaxed transition-colors"
            />
            <p className="text-[11px] text-slate-500 mt-1">Click a merge field on the right to insert it at the cursor.</p>
          </section>

          {/* Settings */}
          <section>
            <p className="text-[11px] uppercase tracking-wider font-medium text-slate-500 mb-2">Settings</p>
            <div className="space-y-1.5">
              <label className={`flex items-center gap-2 ${isSystem ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}>
                <input
                  type="checkbox"
                  checked={interviewerVisible}
                  onChange={e => setInterviewerVisible(e.target.checked)}
                  disabled={isSystem}
                  data-testid="template-interviewer-visible"
                  className="w-3.5 h-3.5 accent-slate-900"
                />
                <span className="text-[13px] text-slate-900">Available to interviewer</span>
                {isSystem && <span className="text-[11px] text-slate-500">· system-managed</span>}
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={active}
                  onChange={e => setActive(e.target.checked)}
                  data-testid="template-active"
                  className="w-3.5 h-3.5 accent-slate-900"
                />
                <span className="text-[13px] text-slate-900">Active</span>
                <span className="text-[11px] text-slate-500">· inactive templates won't appear in the picker</span>
              </label>
            </div>
          </section>

          {/* Advanced — read-only for post-discovery */}
          {hasAdvanced && template?.toggles?.length > 0 && (
            <section>
              <div className="flex items-center justify-between mb-2">
                <p className="text-[11px] uppercase tracking-wider font-medium text-slate-500">Advanced</p>
                <span className="ops-pill bg-blue-50 text-blue-700 border-blue-100">Read-only</span>
              </div>
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 space-y-1.5">
                {template.toggles.map((t, i) => (
                  <div key={i} className="text-[12px] flex items-center gap-2">
                    <span className="font-medium text-slate-900">{t.label}</span>
                    <span className="text-slate-500">·</span>
                    <span className="text-slate-500">{t.type}{t.options ? ` · ${t.options.length} options` : ''}</span>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* MERGE FIELD SIDEBAR */}
        <aside className="px-5 py-4 bg-slate-50/40 lg:sticky lg:top-0 self-start max-h-[68vh] lg:overflow-y-auto">
          <p className="text-[11px] uppercase tracking-wider font-medium text-slate-500 mb-0.5">Merge fields</p>
          <p className="text-[11px] text-slate-500 mb-2">Click to insert at cursor</p>
          <div className="flex flex-wrap gap-1.5">
            {MERGE_FIELDS.map(([f, help]) => (
              <button
                key={f}
                type="button"
                onClick={() => insertMerge(f)}
                title={help}
                data-testid={`merge-chip-${f}`}
                className="text-[11px] font-mono text-slate-700 bg-white border border-slate-200 hover:border-slate-900 hover:bg-slate-900 hover:text-white rounded-md px-2 py-1 transition-colors"
              >
                {`{${f}}`}
              </button>
            ))}
          </div>
        </aside>
      </div>
    </OpsModal>
  );
}
